import React from 'react';
import { Search, RefreshCw, Sparkles, X, FileText, PanelRight } from 'lucide-react';

type GraphFiltersProps = {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  onRefresh: () => void;
  onEnrich: () => void;
  onViewDetails: () => void;
  selectedEntityName?: string | null;
  isLoading?: boolean;
  isEnriching?: boolean;
  docFilter?: string;
  onClearDocFilter?: () => void;
};

export const GraphFilters: React.FC<GraphFiltersProps> = ({
  searchQuery,
  onSearchChange,
  onRefresh,
  onEnrich,
  onViewDetails,
  selectedEntityName,
  isLoading,
  isEnriching,
  docFilter,
  onClearDocFilter,
}) => {
  return (
    <div className="flex items-center gap-3 px-4 py-2.5 bg-white border-b border-gray-200">
      {docFilter && (
        <div className="flex items-center gap-1 px-2 py-1 bg-indigo-50 border border-indigo-200 rounded-md text-xs text-indigo-700 max-w-[200px]">
          <FileText className="w-3 h-3 flex-shrink-0" />
          <span className="truncate" title={docFilter}>{docFilter}</span>
          <button
            onClick={onClearDocFilter}
            className="flex-shrink-0 ml-0.5 text-indigo-400 hover:text-indigo-700"
            title="Clear document filter"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      )}

      <div className="relative flex-1 max-w-sm">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
        <input
          type="text"
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={docFilter ? 'Search entities in document...' : 'Search entities...'}
          className="w-full pl-8 pr-3 py-1.5 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-900 placeholder-gray-400 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 focus:bg-white"
        />
      </div>

      <button
        onClick={onViewDetails}
        disabled={!selectedEntityName}
        title={selectedEntityName ? `View details for ${selectedEntityName}` : 'Click an entity first'}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-indigo-600 text-white text-sm rounded-md hover:bg-indigo-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
      >
        <PanelRight className="w-3.5 h-3.5" />
        {selectedEntityName ? `${selectedEntityName.length > 16 ? selectedEntityName.slice(0, 16) + '…' : selectedEntityName}` : 'View Details'}
      </button>

      <button
        onClick={onEnrich}
        disabled={isEnriching}
        title="AI discovers cross-document relationships"
        className="flex items-center gap-1.5 px-3 py-1.5 bg-purple-600 text-white text-sm rounded-md hover:bg-purple-700 transition-colors disabled:opacity-50"
      >
        <Sparkles className={`w-3.5 h-3.5 ${isEnriching ? 'animate-pulse' : ''}`} />
        {isEnriching ? 'Enriching...' : 'Enrich'}
      </button>

      <button
        onClick={onRefresh}
        disabled={isLoading}
        className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 border border-gray-200 text-sm rounded-md hover:bg-gray-200 transition-colors disabled:opacity-50"
      >
        <RefreshCw className={`w-3.5 h-3.5 ${isLoading ? 'animate-spin' : ''}`} />
        Refresh
      </button>
    </div>
  );
};
